/**
 * No POST normal, o `LogoutView` do Django (via `RedirectURLMixin`) valida
 * host/esquema de `next` antes de redirecionar. No branch sem cookie de
 * `logoutPost` (abaixo) o redirect é feito direto pelo browser, sem passar
 * pelo Django — essa validação não existe mais no caminho e precisa ser
 * refeita aqui, senão `next` vindo de uma origem não confiável (query param,
 * deep link) vira open redirect.
 *
 * Comparação por caractere não basta: o parser de URL do browser (WHATWG)
 * remove tab/LF/CR de qualquer posição da string antes de resolver a URL, então
 * `"/\t/evil.com"` passaria numa checagem de `next[1]` mas viraria `//evil.com`
 * (protocol-relative) quando atribuído a `location.href`. Por isso a validação
 * usa o próprio parser (`new URL`) — a normalização acontece ANTES da
 * comparação de origem, não depois — e devolve o caminho já normalizado
 * (nunca a string crua) para não reintroduzir os caracteres removidos.
 *
 * `currentOrigin` é parâmetro explícito (não lê `window.location.origin`
 * internamente) para a função ficar pura e testável sem DOM/jsdom — quem
 * chama em runtime passa `window.location.origin`.
 *
 * @returns o caminho normalizado (pathname+search+hash) se `next` for
 * same-origin, ou `null` (URL malformada, esquema diferente, origem diferente
 * — inclui `javascript:` e afins, cujo `.origin` é `"null"`).
 */
export function sanitizeNext(next: string | undefined, currentOrigin: string): string | null {
  if (!next) return null;
  let url: URL;
  try {
    url = new URL(next, currentOrigin);
  } catch {
    return null;
  }
  if (url.origin !== currentOrigin) return null;
  return url.pathname + url.search + url.hash;
}

/**
 * Logout via POST com CSRF na view do Django (`/logout/`).
 *
 * Extraído originalmente de `components/account-popover/account-popover.ts`
 * (classe vanilla removida na Fase 3 Frente C — porte React em
 * `react/layout/account-popover.tsx`, que importa daqui) porque o gate de
 * permissão em `main.ts` precisa do mesmo comportamento — sessão só morre
 * server-side, e um GET em /logout/ não invalida nada.
 *
 * `next` vai como campo do POST: `LogoutView` estende `RedirectURLMixin`, que
 * lê `next` e valida host/esquema antes de redirecionar. Sem `next`, cai no
 * `LOGOUT_REDIRECT_URL` do settings.
 */
export function logoutPost(next?: string): void {
  const csrf = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith("csrftoken="));

  // Sem cookie CSRF não há como montar um POST válido — o `CsrfViewMiddleware`
  // barraria com 403 "CSRF cookie not set" antes mesmo de `LogoutView.post()`
  // rodar, então a sessão já não morria nesse cenário no comportamento
  // anterior; este guard só troca o 403 cru por um redirect.
  // Caso-limite conhecido e aceito: se algo apagar `csrftoken` (não é
  // HttpOnly) sem tocar em `sessionid` (é HttpOnly, ex.: extensão/bookmarklet
  // limpando via `document.cookie`), a sessão continua viva no servidor e
  // este redirect não a invalida. A falha não é silenciosa: com sessão viva,
  // `PortalLoginView.redirect_authenticated_user` manda o GET em `/login/`
  // de volta pro painel em vez da tela de login.
  if (!csrf) {
    window.location.href = sanitizeNext(next, window.location.origin) ?? "/login/";
    return;
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/logout/";

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "csrfmiddlewaretoken";
  input.value = decodeURIComponent(csrf.split("=")[1]);
  form.appendChild(input);

  if (next) {
    const nextInput = document.createElement("input");
    nextInput.type = "hidden";
    nextInput.name = "next";
    nextInput.value = next;
    form.appendChild(nextInput);
  }

  document.body.appendChild(form);
  form.submit();
}
